import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check, Calendar as CalendarIcon, Clock, Phone, Video, MessageCircle } from "lucide-react";

/* Small bilingual dictionary — self-contained so this page is safe regardless of app-wide i18n coverage */
const T = {
  en: {
    callback_title: "Request a Callback",
    callback_lede: "Choose a date and time when you'd like us to call you. We'll confirm within one business hour.",
    strategy_title: "Book a Strategy Call",
    strategy_lede: "30 minutes with a senior strategist — we'll map the fastest path from where you are today to more calls, leads, and customers.",
    select_date: "Select a Date",
    select_time: "Select a Time",
    business: "Business Name",
    name: "Contact Name *",
    phone: "Phone *",
    email: "Email *",
    city: "City",
    state: "State",
    website: "Website",
    industry: "Industry",
    service: "Service Interested In",
    budget: "Approximate Marketing Budget",
    goal: "Primary Business Goal",
    method: "Preferred Contact Method",
    tz: "Time Zone",
    notes_short: "What do you need help with?",
    notes_long: "Notes / Questions",
    cta_callback: "Request Callback",
    cta_strategy: "Book Strategy Call",
    sending: "Sending…",
    no_slots: "No available times for this date. Try another day.",
    booked_title: "You're booked!",
    booked_body: "Thank you for scheduling with Yosi Media Studio. We have received your request and will contact you at your selected date and time.",
    method_phone: "Phone Call", method_zoom: "Zoom / Video Call", method_meet: "Google Meet", method_other: "Other",
  },
  es: {
    callback_title: "Solicitar una llamada",
    callback_lede: "Elija la fecha y hora en que desea que le llamemos. Confirmaremos en una hora hábil.",
    strategy_title: "Agendar una llamada estratégica",
    strategy_lede: "30 minutos con un estratega senior — mapearemos el camino más rápido para generar más llamadas, leads y clientes.",
    select_date: "Seleccione una fecha",
    select_time: "Seleccione una hora",
    business: "Nombre del negocio",
    name: "Nombre de contacto *",
    phone: "Teléfono *",
    email: "Email *",
    city: "Ciudad",
    state: "Estado",
    website: "Sitio web",
    industry: "Industria",
    service: "Servicio de interés",
    budget: "Presupuesto de marketing aproximado",
    goal: "Objetivo principal del negocio",
    method: "Método de contacto preferido",
    tz: "Zona horaria",
    notes_short: "¿En qué necesita ayuda?",
    notes_long: "Notas / Preguntas",
    cta_callback: "Solicitar llamada",
    cta_strategy: "Agendar llamada",
    sending: "Enviando…",
    no_slots: "No hay horarios disponibles ese día. Pruebe otra fecha.",
    booked_title: "¡Cita reservada!",
    booked_body: "Gracias por agendar con Yosi Media Studio. Hemos recibido su solicitud y nos comunicaremos con usted en la fecha y hora seleccionadas.",
    method_phone: "Llamada telefónica", method_zoom: "Zoom / Videollamada", method_meet: "Google Meet", method_other: "Otro",
  },
};

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function BookScheduling({ appointmentType }) {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get("lang") === "es" ? "es" : "en";
  const t = T[lang];
  const isStrategy = appointmentType === "strategy_call";
  const title = isStrategy ? t.strategy_title : t.callback_title;
  const lede  = isStrategy ? t.strategy_lede : t.callback_lede;

  const [date, setDate] = useState(addDays(today(), 1));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [sending, setSending] = useState(false);
  const [booked, setBooked] = useState(false);
  const [form, setForm] = useState({
    time: "", timezone: "America/Chicago", contact_method: "phone",
    business_name: "", contact_name: "", phone: "", email: "",
    city: "", state: "", website: "",
    industry: "", service_interested: "", budget: "", primary_goal: "",
    notes: "",
  });

  useEffect(() => {
    setLoadingSlots(true);
    api.get(`/public/scheduling/slots?date=${date}`)
      .then(({ data }) => setSlots(Array.isArray(data.slots) ? data.slots : []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
    setForm((f) => ({ ...f, time: "" }));
  }, [date]);

  const dateOptions = useMemo(() => Array.from({ length: 21 }, (_, i) => addDays(today(), i + 1)), []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time) { toast.error(t.select_time); return; }
    setSending(true);
    try {
      await api.post("/public/scheduling/book", {
        appointment_type: appointmentType,
        date, lang, ...form,
      });
      setBooked(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d === "slot_taken") toast.error(lang === "es" ? "Ese horario acaba de reservarse. Elija otro." : "That slot was just booked. Please choose another.");
      else if (d === "day_full") toast.error(lang === "es" ? "Ese día ya está lleno." : "That day is fully booked.");
      else toast.error(formatApiErrorDetail(d) || "Booking failed");
    } finally { setSending(false); }
  };

  if (booked) {
    return (
      <MarketingLayout>
        <Seo title={`${t.booked_title} · Yosi Media Studio`} description={t.booked_body} />
        <section className="max-w-2xl mx-auto px-6 sm:px-8 py-24 text-center" data-testid="booking-success">
          <div className="w-16 h-16 mx-auto rounded-full grid place-items-center mb-6" style={{ background: "rgba(16,185,129,0.15)", color: "#34D399" }}>
            <Check className="w-8 h-8" />
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-4">{t.booked_title}</h1>
          <p className="text-slate-300 leading-relaxed mb-8">{t.booked_body}</p>
          <Link to="/" className="btn-scale h-11 px-6 rounded-full font-bold text-sm text-white inline-flex items-center gap-2"
                style={{ background: "#2B87F5" }}>← {lang === "es" ? "Volver al inicio" : "Back to home"}</Link>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <Seo title={`${title} · Yosi Media Studio`} description={lede} />
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-16" data-testid={isStrategy ? "book-strategy-page" : "book-callback-page"}>
        <div className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: "#2B87F5" }}>
          {isStrategy ? "Strategy Call" : "Callback"}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-white leading-tight mb-4">{title}</h1>
        <p className="max-w-2xl text-slate-400 leading-relaxed mb-10">{lede}</p>

        <div className="flex items-center gap-3 mb-8 text-xs" data-testid="lang-toggle">
          <a href={`?lang=en`} className={`px-3 py-1 rounded-full border ${lang === "en" ? "border-blue-500 text-white bg-blue-500/10" : "border-slate-700 text-slate-400"}`}>EN</a>
          <a href={`?lang=es`} className={`px-3 py-1 rounded-full border ${lang === "es" ? "border-blue-500 text-white bg-blue-500/10" : "border-slate-700 text-slate-400"}`}>ES</a>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="booking-form">
          {/* Left — date + time */}
          <div className="lg:col-span-5 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CalendarIcon className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-semibold text-white">{t.select_date}</span>
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="date-picker">
                {dateOptions.map((d) => {
                  const dt = new Date(d + "T00:00:00");
                  const label = dt.toLocaleDateString(lang === "es" ? "es-US" : "en-US", { weekday: "short", month: "short", day: "numeric" });
                  const active = d === date;
                  return (
                    <button type="button" key={d} onClick={() => setDate(d)} data-testid={`date-${d}`}
                            className={`px-2 py-2 rounded-lg text-xs border transition-colors ${active ? "border-blue-500 bg-blue-500/10 text-white" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-semibold text-white">{t.select_time}</span>
              </div>
              {loadingSlots ? (
                <div className="text-sm text-slate-500">…</div>
              ) : slots.length === 0 ? (
                <div className="text-sm text-slate-500 rounded-lg border border-slate-800 bg-slate-900/40 p-4" data-testid="no-slots">{t.no_slots}</div>
              ) : (
                <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                  {slots.map((s) => (
                    <button type="button" key={s} onClick={() => setForm({ ...form, time: s })}
                            data-testid={`slot-${s}`}
                            className={`px-2 py-2 rounded-lg text-xs border transition-colors ${form.time === s ? "border-blue-500 bg-blue-500/10 text-white" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right — client details */}
          <div className="lg:col-span-7 space-y-3">
            <Row>
              <Fld label={t.business}><input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="ipt" data-testid="fld-business" /></Fld>
              <Fld label={t.name}><input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="ipt" data-testid="fld-name" /></Fld>
              <Fld label={t.phone}><input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ipt" data-testid="fld-phone" /></Fld>
              <Fld label={t.email}><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ipt" data-testid="fld-email" /></Fld>
              <Fld label={t.city}><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="ipt" data-testid="fld-city" /></Fld>
              <Fld label={t.state}><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="ipt" data-testid="fld-state" /></Fld>
              <Fld label={t.website}><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="ipt" data-testid="fld-website" /></Fld>
              <Fld label={t.service}><input value={form.service_interested} onChange={(e) => setForm({ ...form, service_interested: e.target.value })} className="ipt" data-testid="fld-service" /></Fld>
              {isStrategy && (<>
                <Fld label={t.industry}><input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="ipt" data-testid="fld-industry" /></Fld>
                <Fld label={t.budget}><input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="ipt" data-testid="fld-budget" /></Fld>
                <div className="sm:col-span-2">
                  <Fld label={t.goal}><input value={form.primary_goal} onChange={(e) => setForm({ ...form, primary_goal: e.target.value })} className="ipt" data-testid="fld-goal" /></Fld>
                </div>
              </>)}
              <Fld label={t.method}>
                <select value={form.contact_method} onChange={(e) => setForm({ ...form, contact_method: e.target.value })} className="ipt" data-testid="fld-method">
                  <option value="phone">{t.method_phone}</option>
                  <option value="zoom">{t.method_zoom}</option>
                  <option value="google_meet">{t.method_meet}</option>
                  <option value="other">{t.method_other}</option>
                </select>
              </Fld>
              <Fld label={t.tz}>
                <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="ipt" data-testid="fld-tz">
                  <option value="America/Chicago">Central (Alabama/Tennessee)</option>
                  <option value="America/New_York">Eastern</option>
                  <option value="America/Denver">Mountain</option>
                  <option value="America/Los_Angeles">Pacific</option>
                </select>
              </Fld>
            </Row>
            <Fld label={isStrategy ? t.notes_long : t.notes_short}>
              <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ipt py-2" data-testid="fld-notes" />
            </Fld>
            <button type="submit" disabled={sending || !form.time} data-testid="booking-submit"
                    className="btn-scale w-full h-12 rounded-full font-bold text-sm text-white inline-flex items-center justify-center gap-2 shadow-[0_14px_36px_rgba(43,135,245,0.4)] disabled:opacity-50"
                    style={{ background: "#2B87F5" }}>
              {sending ? t.sending : (isStrategy ? t.cta_strategy : t.cta_callback)} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      </section>
      <style>{`.ipt{width:100%;height:2.5rem;background:#020617;border:1px solid #1e293b;border-radius:0.375rem;padding:0 0.75rem;font-size:0.875rem;color:#f1f5f9;outline:none}.ipt:focus{border-color:#3b82f6}`}</style>
    </MarketingLayout>
  );
}

function Row({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function Fld({ label, children }) { return <label className="block"><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{label}</span>{children}</label>; }
